import React from 'react';
import { Box, Container, Typography, Paper, Divider, Link, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { useTheme } from '@mui/material/styles';

const PrivacyPolicy = () => {
  const theme = useTheme();

  return (
    <Box sx={{ 
      minHeight: '100vh',
      bgcolor: 'background.default',
      py: { xs: 4, md: 8 }
    }}>
      <Container maxWidth="md">
        <Paper 
          elevation={0}
          sx={{ 
            p: { xs: 3, md: 6 },
            borderRadius: 3,
            border: `1px solid ${theme.palette.divider}`,
            background: theme.palette.mode === 'dark' 
              ? 'linear-gradient(135deg, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0.05) 100%)'
              : 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,1) 100%)',
            backdropFilter: 'blur(10px)'
          }}
        >
          {/* Header */}
          <Box sx={{ mb: 6, textAlign: 'center' }}>
            <Typography 
              variant="h3" 
              component="h1" 
              gutterBottom
              sx={{ 
                fontWeight: 700,
                background: theme.palette.mode === 'dark'
                  ? 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)'
                  : 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 2
              }}
            >
              Privacy Policy
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
              <strong>Last Updated:</strong> October 31, 2025
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto', mt: 2 }}>
              This Privacy Notice for HypeCircle ('we', 'us', or 'our') describes how and why we might access, collect, store, use, and/or share ('process') your personal information when you use our Services.
            </Typography>
          </Box>

          <Divider sx={{ mb: 4 }} />

          {/* Summary */}
          <Section title="Summary of Key Points">
            <Typography paragraph sx={{ fontStyle: 'italic', color: 'text.secondary', mb: 2 }}>
              This summary provides key points from our Privacy Notice. You can find out more details about any of these topics by reading the full policy below.
            </Typography>
            <List items={[
              'We collect personal information that you provide to us such as email addresses, usernames, passwords, and contact data',
              'We do not process sensitive personal information',
              'We do not collect any information from third parties',
              'We process your information to provide, improve, and administer our Services, communicate with you, for security and fraud prevention, and to comply with law',
              'We may share information in specific situations and with specific third parties including AWS, Google, Facebook, and TMDB',
              'We have adequate organizational and technical processes to protect your personal information',
              'You have rights regarding your personal information depending on your location',
              'The easiest way to exercise your rights is by contacting us at hype.circle.main@gmail.com'
            ]} />
          </Section>

          {/* 1. Information Collection */}
          <Section title="1. What Information Do We Collect?">
            <SubSection title="Personal Information You Disclose to Us">
              <Typography paragraph>
                We collect personal information that you voluntarily provide to us when you register on the Services, express an interest in obtaining information about us or our products and Services, when you participate in activities on the Services, or otherwise when you contact us.
              </Typography>
              <Typography paragraph sx={{ fontWeight: 500 }}>
                Personal Information Provided by You:
              </Typography>
              <List items={[
                'Email addresses',
                'Usernames',
                'Passwords',
                'Contact or authentication data',
                'Display name, bio, avatar, website (profile information)',
                'Posts, comments, reviews, and other user-generated content'
              ]} />
              <Typography paragraph sx={{ mt: 2 }}>
                <strong>Sensitive Information:</strong> We do not process sensitive information.
              </Typography>
            </SubSection>

            <SubSection title="Social Media Login Data">
              <Typography paragraph>
                We may provide you with the option to register with us using your existing social media account details, like your Facebook, Google, or other social media account. If you choose to register in this way, we will receive certain profile information about you from the social media provider, including:
              </Typography>
              <List items={[
                'Name, email address, profile picture',
                'Friends list (if you grant permission)',
                'Other information you choose to make public on such social media platform'
              ]} />
            </SubSection>

            <SubSection title="Application Data">
              <Typography paragraph>
                If you use our application(s), we may also collect the following information if you choose to provide us with access or permission:
              </Typography>
              <List items={[
                'Mobile Device Data: Device ID, model, manufacturer, operating system, version information',
                'Device and application identification numbers',
                'Browser type and version',
                'Hardware model, Internet service provider and/or mobile carrier',
                'Internet Protocol (IP) address',
                'Phone network information associated with your mobile device'
              ]} />
              <Typography paragraph sx={{ mt: 2, fontSize: '0.9rem', color: 'text.secondary' }}>
                This information is primarily needed to maintain the security and operation of our application(s), for troubleshooting, and for our internal analytics and reporting purposes.
              </Typography>
            </SubSection>

            <SubSection title="Information Automatically Collected">
              <Typography paragraph>
                We automatically collect certain information when you visit, use, or navigate the Services. This information does not reveal your specific identity but may include:
              </Typography>
              <List items={[
                'Log and Usage Data: IP address, browser type, device information, pages viewed, search queries, date/time stamps',
                'Device Data: Computer, phone, tablet information including IP address, device IDs, browser type, hardware model, operating system',
                'Location Data: Information about your device\'s location (precise or imprecise) based on IP address or GPS'
              ]} />
              <Typography paragraph sx={{ mt: 2 }}>
                Like many businesses, we also collect information through cookies and similar technologies. You can find out more in our Cookie Notice:{' '}
                <Link href="https://hypecircle.co.in/cookie-policy" target="_blank" color="primary">
                  https://hypecircle.co.in/cookie-policy
                </Link>
              </Typography>
            </SubSection>

            <SubSection title="Google API">
              <Typography paragraph>
                Our use of information received from Google APIs will adhere to{' '}
                <Link href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" color="primary">
                  Google API Services User Data Policy
                </Link>
                , including the Limited Use requirements.
              </Typography>
            </SubSection>

            <SubSection title="Facebook Application Data">
              <Typography paragraph>
                We by default access your Facebook basic account information, including your name, email, gender, birthday, current city, and profile picture URL, as well as other information that you choose to make public. We may also request access to other permissions related to your account, such as friends, check-ins, and likes, and you may choose to grant or deny us access to each individual permission.
              </Typography>
            </SubSection>
          </Section>

          {/* 2. How We Process */}
          <Section title="2. How Do We Process Your Information?">
            <Typography paragraph>
              We process your personal information for a variety of reasons, depending on how you interact with our Services, including:
            </Typography>
            <List items={[
              'To facilitate account creation and authentication and otherwise manage user accounts',
              'To deliver and facilitate delivery of services to the user',
              'To respond to user inquiries/offer support to users',
              'To send administrative information to you (details about our products and services, changes to our terms and policies)',
              'To enable user-to-user communications (if you choose to use any of our offerings that allow for communication with another user)',
              'To request feedback and to contact you about your use of our Services',
              'To protect our Services (fraud monitoring and prevention, keeping our Services safe and secure)',
              'To identify usage trends (to better understand how our Services are being used so we can improve them)',
              'To save or protect an individual\'s vital interest (when necessary to prevent harm)'
            ]} />
          </Section>

          {/* 3. Legal Bases */}
          <Section title="3. What Legal Bases Do We Rely On to Process Your Personal Information?">
            <Typography paragraph>
              We only process your personal information when we believe it is necessary and we have a valid legal reason (i.e., legal basis) to do so under applicable law, like with your consent, to comply with laws, to provide you with services to enter into or fulfill our contractual obligations, to protect your rights, or to fulfill our legitimate business interests.
            </Typography>
            
            <SubSection title="For Users in the EU/UK">
              <Typography paragraph>
                The GDPR and UK GDPR require us to explain the valid legal bases we rely on. We may rely on the following legal bases:
              </Typography>
              <List items={[
                'Consent: We may process your information if you have given us permission to use your personal information for a specific purpose',
                'Performance of a Contract: When we believe it is necessary to fulfill our contractual obligations to you',
                'Legitimate Interests: When we believe it is reasonably necessary to achieve our legitimate business interests',
                'Legal Obligations: Where we believe it is necessary for compliance with our legal obligations',
                'Vital Interests: Where we believe it is necessary to protect your vital interests or those of a third party'
              ]} />
            </SubSection>

            <SubSection title="For Users in Canada">
              <Typography paragraph>
                We may process your information if you have given us specific permission (express consent) or in situations where your permission can be inferred (implied consent). You can withdraw your consent at any time.
              </Typography>
              <Typography paragraph>
                In some exceptional cases, we may be legally permitted under applicable law to process your information without your consent, including:
              </Typography>
              <List items={[
                'If collection is clearly in the interests of an individual and consent cannot be obtained in a timely way',
                'For investigations and fraud detection and prevention',
                'For business transactions provided certain conditions are met',
                'If it is contained in a witness statement and the collection is necessary to assess, process, or settle an insurance claim',
                'For identifying injured, ill, or deceased persons and communicating with next of kin',
                'If we have reasonable grounds to believe an individual has been, is, or may be victim of financial abuse',
                'If it is reasonable to expect collection and use with consent would compromise the availability or accuracy of the information',
                'If disclosure is required to comply with a subpoena, warrant, court order, or rules of the court',
                'If it was produced by an individual in the course of their employment, business, or profession',
                'If the collection is solely for journalistic, artistic, or literary purposes',
                'If the information is publicly available and is specified by the regulations'
              ]} />
            </SubSection>
          </Section>

          {/* 4. Information Sharing */}
          <Section title="4. When and With Whom Do We Share Your Personal Information?">
            <Typography paragraph>
              We may share information in specific situations and with specific third parties. We have contracts in place with our third parties, which are designed to help safeguard your personal information.
            </Typography>
            
            <SubSection title="Third-Party Service Providers">
              <Typography paragraph>
                The third parties we may share personal information with include:
              </Typography>
              <List items={[
                'Allow Users to Connect to Third-Party Accounts: Google account and Facebook account',
                'Cloud Computing Services: Amazon Web Services (AWS) and Google Cloud Platform',
                'Content Optimization: YouTube video embed',
                'Functionality and Infrastructure Optimization: Amazon Web Services',
                'User Account Registration and Authentication: Google Sign-In, Facebook Login, and Auth0',
                'Web and Mobile Analytics: Google Analytics for Firebase',
                'Content Data API: The Movie Database (TMDB)'
              ]} />
            </SubSection>

            <SubSection title="Other Sharing Situations">
              <List items={[
                'Business Transfers: In connection with any merger, sale of company assets, financing, or acquisition',
                'Other Users: When you share personal information or interact with public areas of the Services',
                'With Your Consent: We may disclose your personal information for any other purpose with your consent'
              ]} />
            </SubSection>
          </Section>

          {/* 5. Cookies */}
          <Section title="5. Do We Use Cookies and Other Tracking Technologies?">
            <Typography paragraph>
              We may use cookies and similar tracking technologies (like web beacons and pixels) to gather information when you interact with our Services. Some online tracking technologies help us maintain the security of our Services and your account, prevent crashes, fix bugs, save your preferences, and assist with basic site functions.
            </Typography>
            <Typography paragraph>
              We also permit third parties and service providers to use online tracking technologies on our Services for analytics and advertising. Specific information about how we use such technologies is set out in our Cookie Notice:{' '}
              <Link href="https://hypecircle.co.in/cookie-policy" target="_blank" color="primary">
                https://hypecircle.co.in/cookie-policy
              </Link>
            </Typography>
            <Typography paragraph>
              <strong>Google Analytics:</strong> We may share your information with Google Analytics to track and analyze the use of the Services. To opt out of being tracked by Google Analytics, visit{' '}
              <Link href="https://tools.google.com/dlpage/gaoptout" target="_blank" color="primary">
                https://tools.google.com/dlpage/gaoptout
              </Link>
            </Typography>
          </Section>

          {/* 6. Social Logins */}
          <Section title="6. How Do We Handle Your Social Logins?">
            <Typography paragraph>
              Our Services offer you the ability to register and log in using your third-party social media account details (like your Facebook or Google logins). Where you choose to do this, we will receive certain profile information about you from your social media provider.
            </Typography>
            <Typography paragraph>
              The profile information we receive may vary depending on the social media provider concerned, but will often include your name, email address, friends list, and profile picture, as well as other information you choose to make public on such a social media platform.
            </Typography>
            <Typography paragraph>
              We will use the information we receive only for the purposes that are described in this Privacy Notice or that are otherwise made clear to you on the relevant Services. Please note that we do not control, and are not responsible for, other uses of your personal information by your third-party social media provider.
            </Typography>
          </Section>

          {/* 7. International Transfers */}
          <Section title="7. Is Your Information Transferred Internationally?">
            <Typography paragraph sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
              In Short: We may transfer, store, and process your information in countries other than your own.
            </Typography>
            <Typography paragraph>
              Our primary servers are located in India (AWS Mumbai region - ap-south-1). However, we also use third-party services that may be hosted in other countries, including the United States. Regardless of your location, please be aware that your information may be transferred to, stored by, and processed by us in our facilities and in the facilities of the third parties with whom we may share your personal information (see 'WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?' above), including facilities in the United States and other countries.
            </Typography>
            <Typography paragraph>
              If you are a resident in the European Economic Area (EEA), United Kingdom (UK), or Switzerland, then these countries may not necessarily have data protection laws as comprehensive as those in your country. However, we will take all necessary measures to protect your personal information in accordance with this Privacy Notice and applicable law.
            </Typography>
            <Typography paragraph>
              <strong>European Commission's Standard Contractual Clauses:</strong> We have implemented measures to protect your personal information, including by using the European Commission's Standard Contractual Clauses for transfers of personal information between our group companies and between us and our third-party providers.
            </Typography>
          </Section>

          {/* 8. Data Retention */}
          <Section title="8. How Long Do We Keep Your Information?">
            <Typography paragraph>
              We will only keep your personal information for as long as it is necessary for the purposes set out in this Privacy Notice, unless a longer retention period is required or permitted by law (such as tax, accounting, or other legal requirements).
            </Typography>
            <Typography paragraph>
              No purpose in this notice will require us keeping your personal information for longer than thirty (30) months past the termination of the user's account.
            </Typography>
            <Typography paragraph>
              When we have no ongoing legitimate business need to process your personal information, we will either delete or anonymize such information, or, if this is not possible, then we will securely store your personal information and isolate it from any further processing until deletion is possible.
            </Typography>
          </Section>

          {/* 9. Data Security */}
          <Section title="9. How Do We Keep Your Information Safe?">
            <Typography paragraph>
              We have implemented appropriate and reasonable technical and organizational security measures designed to protect the security of any personal information we process. These include:
            </Typography>
            <List items={[
              'Encryption in transit (HTTPS/TLS)',
              'Encryption at rest for sensitive data',
              'Secure authentication (AWS Cognito)',
              'Regular security audits and monitoring',
              'Access controls and authentication',
              'Data stored on AWS servers in Mumbai, India (ap-south-1 region)'
            ]} />
            <Typography paragraph sx={{ mt: 2 }}>
              However, despite our safeguards and efforts to secure your information, no electronic transmission over the Internet or information storage technology can be guaranteed to be 100% secure, so we cannot promise or guarantee that hackers, cybercriminals, or other unauthorized third parties will not be able to defeat our security and improperly collect, access, steal, or modify your information.
            </Typography>
          </Section>

          {/* 10. Minors */}
          <Section title="10. Do We Collect Information From Minors?">
            <Typography paragraph>
              We do not knowingly collect, solicit data from, or market to children under 18 years of age or the equivalent age as specified by law in your jurisdiction, nor do we knowingly sell such personal information.
            </Typography>
            <Typography paragraph>
              By using the Services, you represent that you are at least 18 or the equivalent age as specified by law in your jurisdiction or that you are the parent or guardian of such a minor and consent to such minor dependent's use of the Services.
            </Typography>
            <Typography paragraph>
              If we learn that personal information from users less than 18 years of age has been collected, we will deactivate the account and take reasonable measures to promptly delete such data from our records. If you become aware of any data we may have collected from children under age 18, please contact us at{' '}
              <Link href="mailto:hype.circle.main@gmail.com" color="primary">
                hype.circle.main@gmail.com
              </Link>
            </Typography>
          </Section>

          {/* 11. Privacy Rights */}
          <Section title="11. What Are Your Privacy Rights?">
            <Typography paragraph>
              Depending on your state of residence in the US or in some regions, such as the European Economic Area (EEA), United Kingdom (UK), Switzerland, and Canada, you have rights that allow you greater access to and control over your personal information.
            </Typography>
            
            <SubSection title="Your Rights May Include">
              <List items={[
                'Right to request access and obtain a copy of your personal information',
                'Right to request rectification or erasure',
                'Right to restrict the processing of your personal information',
                'Right to data portability (if applicable)',
                'Right not to be subject to automated decision-making',
                'Right to object to the processing of your personal information',
                'Right to withdraw consent at any time'
              ]} />
            </SubSection>

            <SubSection title="For EEA, UK, and Swiss Residents">
              <Typography paragraph>
                If you are located in the EEA, UK, or Switzerland and you believe we are unlawfully processing your personal information, you also have the right to complain to your Member State data protection authority or UK data protection authority.
              </Typography>
              <Typography paragraph>
                If you are located in Switzerland, you may contact the Federal Data Protection and Information Commissioner.
              </Typography>
            </SubSection>

            <SubSection title="For Indian Residents">
              <Typography paragraph>
                Indian users have the right to:
              </Typography>
              <List items={[
                'Access their personal data',
                'Correct inaccurate personal data',
                'Request erasure of personal data',
                'Nominate another person to exercise rights in case of death or incapacity',
                'Withdraw consent for data processing',
                'File complaints with the Data Protection Board of India'
              ]} />
            </SubSection>

            <SubSection title="Withdrawing Your Consent">
              <Typography paragraph>
                If we are relying on your consent to process your personal information, which may be express and/or implied consent depending on the applicable law, you have the right to withdraw your consent at any time. You can withdraw your consent at any time by contacting us using the contact details provided in this notice.
              </Typography>
              <Typography paragraph>
                However, please note that this will not affect the lawfulness of the processing before its withdrawal nor, when applicable law allows, will it affect the processing of your personal information conducted in reliance on lawful processing grounds other than consent.
              </Typography>
            </SubSection>

            <SubSection title="Account Information">
              <Typography paragraph>
                If you would at any time like to review or change the information in your account or terminate your account, you can:
              </Typography>
              <List items={[
                'Log in to your account settings and update your user account',
                'Contact hype.circle.main@gmail.com'
              ]} />
              <Typography paragraph sx={{ mt: 2 }}>
                Upon your request to terminate your account, we will deactivate or delete your account and information from our active databases. However, we may retain some information in our files to prevent fraud, troubleshoot problems, assist with any investigations, enforce our legal terms and/or comply with applicable legal requirements.
              </Typography>
            </SubSection>
          </Section>

          {/* 12. Do Not Track */}
          <Section title="12. Controls for Do-Not-Track Features">
            <Typography paragraph>
              Most web browsers and some mobile operating systems and mobile applications include a Do-Not-Track ('DNT') feature or setting you can activate to signal your privacy preference not to have data about your online browsing activities monitored and collected.
            </Typography>
            <Typography paragraph>
              At this stage, no uniform technology standard for recognizing and implementing DNT signals has been finalized. As such, we do not currently respond to DNT browser signals or any other mechanism that automatically communicates your choice not to be tracked online.
            </Typography>
          </Section>

          {/* 13. US Residents */}
          <Section title="13. Do United States Residents Have Specific Privacy Rights?">
            <Typography paragraph>
              If you are a resident of California, Colorado, Connecticut, Delaware, Florida, Indiana, Iowa, Kentucky, Maryland, Minnesota, Montana, Nebraska, New Hampshire, New Jersey, Oregon, Rhode Island, Tennessee, Texas, Utah, or Virginia, you may have the right to request access to and receive details about the personal information we maintain about you and how we have processed it, correct inaccuracies, get a copy of, or delete your personal information.
            </Typography>

            <SubSection title="Categories of Personal Information We Collect">
              <Typography paragraph>
                The table below shows the categories of personal information we have collected in the past twelve (12) months:
              </Typography>
              <TableContainer sx={{ mt: 3, mb: 3, border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Examples</TableCell>
                      <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>Collected</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>A. Identifiers</TableCell>
                      <TableCell>Contact details, such as real name, alias, postal address, telephone or mobile contact number, unique personal identifier, online identifier, Internet Protocol address, email address, and account name</TableCell>
                      <TableCell sx={{ textAlign: 'center', color: 'success.main', fontWeight: 600 }}>YES</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>B. Personal information (CA Customer Records)</TableCell>
                      <TableCell>Name, contact information, education, employment, employment history, and financial information</TableCell>
                      <TableCell sx={{ textAlign: 'center', color: 'error.main', fontWeight: 600 }}>NO</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>C. Protected classification characteristics</TableCell>
                      <TableCell>Gender, age, date of birth, race and ethnicity, national origin, marital status, and other demographic data</TableCell>
                      <TableCell sx={{ textAlign: 'center', color: 'error.main', fontWeight: 600 }}>NO</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>D. Commercial information</TableCell>
                      <TableCell>Transaction information, purchase history, financial details, and payment information</TableCell>
                      <TableCell sx={{ textAlign: 'center', color: 'error.main', fontWeight: 600 }}>NO</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>E. Biometric information</TableCell>
                      <TableCell>Fingerprints and voiceprints</TableCell>
                      <TableCell sx={{ textAlign: 'center', color: 'error.main', fontWeight: 600 }}>NO</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>F. Internet or network activity</TableCell>
                      <TableCell>Browsing history, search history, online behaviour, interest data, and interactions with our and other websites, applications, systems, and advertisements</TableCell>
                      <TableCell sx={{ textAlign: 'center', color: 'success.main', fontWeight: 600 }}>YES</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>G. Geolocation data</TableCell>
                      <TableCell>Device location</TableCell>
                      <TableCell sx={{ textAlign: 'center', color: 'success.main', fontWeight: 600 }}>YES</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>H. Audio, electronic, sensory information</TableCell>
                      <TableCell>Images and audio, video or call recordings created in connection with our business activities</TableCell>
                      <TableCell sx={{ textAlign: 'center', color: 'success.main', fontWeight: 600 }}>YES</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>I. Professional or employment information</TableCell>
                      <TableCell>Business contact details in order to provide you our Services at a business level or job title, work history, and professional qualifications if you apply for a job with us</TableCell>
                      <TableCell sx={{ textAlign: 'center', color: 'error.main', fontWeight: 600 }}>NO</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>J. Education Information</TableCell>
                      <TableCell>Student records and directory information</TableCell>
                      <TableCell sx={{ textAlign: 'center', color: 'error.main', fontWeight: 600 }}>NO</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>K. Inferences</TableCell>
                      <TableCell>Inferences drawn from any of the collected personal information listed above to create a profile or summary about, for example, an individual's preferences and characteristics</TableCell>
                      <TableCell sx={{ textAlign: 'center', color: 'success.main', fontWeight: 600 }}>YES</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>L. Sensitive personal Information</TableCell>
                      <TableCell>-</TableCell>
                      <TableCell sx={{ textAlign: 'center', color: 'error.main', fontWeight: 600 }}>NO</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
              <Typography paragraph>
                We may also collect other personal information outside of these categories through instances where you interact with us in person, online, or by phone or mail in the context of:
              </Typography>
              <List items={[
                'Receiving help through our customer support channels',
                'Participation in customer surveys or contests',
                'Facilitation in the delivery of our Services and to respond to your inquiries'
              ]} />
              <Typography paragraph sx={{ mt: 2, fontWeight: 500 }}>
                We will use and retain the collected personal information as needed to provide the Services or for:
              </Typography>
              <List items={[
                'Category A - As long as the user has an account with us, plus 30 days after account deletion',
                'Category F - As long as the user has an account with us',
                'Category G - As long as the user has an account with us',
                'Category H - As long as the user has an account with us',
                'Category K - As long as the user has an account with us'
              ]} />
            </SubSection>

            <SubSection title="Sources of Personal Information">
              <Typography paragraph>
                Learn more about the sources of personal information we collect in 'WHAT INFORMATION DO WE COLLECT?'
              </Typography>
            </SubSection>

            <SubSection title="How We Use and Share Personal Information">
              <Typography paragraph>
                Learn more about how we use your personal information in the section, 'HOW DO WE PROCESS YOUR INFORMATION?'
              </Typography>
              <Typography paragraph sx={{ mt: 2 }}>
                We collect and share your personal information through:
              </Typography>
              <List items={[
                'Beacons/Pixels/Tags',
                'Social media plugins: Google OAuth and Facebook. We use social media features, such as a \'Like\' button, and widgets, such as a \'Share\' button, in our Services. Such features may process your Internet Protocol (IP) address and track which page you are visiting on our website.'
              ]} />
            </SubSection>

            <SubSection title="Will your information be shared with anyone else?">
              <Typography paragraph>
                We may disclose your personal information with our service providers pursuant to a written contract between us and each service provider. Learn more about how we disclose personal information in the section, 'WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?'
              </Typography>
              <Typography paragraph>
                We may use your personal information for our own business purposes, such as for undertaking internal research for technological development and demonstration. This is not considered to be 'selling' of your personal information.
              </Typography>
              <Typography paragraph>
                We have not sold or shared any personal information to third parties for a business or commercial purpose in the preceding twelve (12) months. We have disclosed the following categories of personal information to third parties for a business or commercial purpose in the preceding twelve (12) months:
              </Typography>
              <List items={[
                'Category A. Identifiers',
                'Category F. Internet or other electronic network activity information',
                'Category G. Geolocation data',
                'Category K. Inferences drawn from collected personal information'
              ]} />
              <Typography paragraph sx={{ mt: 2 }}>
                The categories of third parties to whom we disclosed personal information for a business or commercial purpose can be found under 'WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?'
              </Typography>
            </SubSection>

            <SubSection title="Your Rights">
              <Typography paragraph>
                You have rights under certain US state data protection laws. However, these rights are not absolute, and in certain cases, we may decline your request as permitted by law. These rights include:
              </Typography>
              <List items={[
                'Right to know whether or not we are processing your personal data',
                'Right to access your personal data',
                'Right to correct inaccuracies in your personal data',
                'Right to request the deletion of your personal data',
                'Right to obtain a copy of the personal data you previously shared with us',
                'Right to non-discrimination for exercising your rights',
                'Right to opt out of the processing of your personal data if it is used for targeted advertising (or sharing as defined under California\'s privacy law), the sale of personal data, or profiling in furtherance of decisions that produce legal or similarly significant effects (\'profiling\')'
              ]} />
              <Typography paragraph sx={{ mt: 2 }}>
                Depending upon the state where you live, you may also have the following rights:
              </Typography>
              <List items={[
                'Right to access the categories of personal data being processed (as permitted by applicable law, including the privacy law in Minnesota)',
                'Right to obtain a list of the categories of third parties to which we have disclosed personal data (as permitted by applicable law, including the privacy law in California, Delaware, and Maryland)',
                'Right to obtain a list of specific third parties to which we have disclosed personal data (as permitted by applicable law, including the privacy law in Minnesota and Oregon)',
                'Right to obtain a list of third parties to which we have sold personal data (as permitted by applicable law, including the privacy law in Connecticut)',
                'Right to review, understand, question, and depending on where you live, correct how personal data has been profiled (as permitted by applicable law, including the privacy law in Connecticut and Minnesota)',
                'Right to limit use and disclosure of sensitive personal data (as permitted by applicable law, including the privacy law in California)',
                'Right to opt out of the collection of sensitive data and personal data collected through the operation of a voice or facial recognition feature (as permitted by applicable law, including the privacy law in Florida)'
              ]} />
            </SubSection>

            <SubSection title="How to Exercise Your Rights">
              <Typography paragraph>
                To exercise these rights, you can contact us by visiting{' '}
                <Link href="https://www.hypecircle.co.in/facebook-data-deletion.html" target="_blank" color="primary">
                  https://www.hypecircle.co.in/facebook-data-deletion.html
                </Link>
                , by emailing us at{' '}
                <Link href="mailto:hype.circle.main@gmail.com" color="primary">
                  hype.circle.main@gmail.com
                </Link>
                , or by referring to the contact details at the bottom of this document.
              </Typography>
              <Typography paragraph>
                Under certain US state data protection laws, you can designate an authorized agent to make a request on your behalf. We may deny a request from an authorized agent that does not submit proof that they have been validly authorized to act on your behalf in accordance with applicable laws.
              </Typography>
            </SubSection>

            <SubSection title="Request Verification">
              <Typography paragraph>
                Upon receiving your request, we will need to verify your identity to determine you are the same person about whom we have the information in our system. We will only use personal information provided in your request to verify your identity or authority to make the request. However, if we cannot verify your identity from the information already maintained by us, we may request that you provide additional information for the purposes of verifying your identity and for security or fraud-prevention purposes.
              </Typography>
              <Typography paragraph>
                If you submit the request through an authorised agent, we may need to collect additional information to verify your identity before processing your request and the agent will need to provide a written and signed permission from you to submit such request on your behalf.
              </Typography>
            </SubSection>

            <SubSection title="Appeals">
              <Typography paragraph>
                Under certain US state data protection laws, if we decline to take action regarding your request, you may appeal our decision by emailing us at{' '}
                <Link href="mailto:hype.circle.main@gmail.com" color="primary">
                  hype.circle.main@gmail.com
                </Link>
                . We will inform you in writing of any action taken or not taken in response to the appeal, including a written explanation of the reasons for the decisions. If your appeal is denied, you may submit a complaint to your state attorney general.
              </Typography>
            </SubSection>

            <SubSection title="California 'Shine The Light' Law">
              <Typography paragraph>
                California Civil Code Section 1798.83, also known as the 'Shine The Light' law, permits our users who are California residents to request and obtain from us, once a year and free of charge, information about categories of personal information (if any) we disclosed to third parties for direct marketing purposes and the names and addresses of all third parties with which we shared personal information in the immediately preceding calendar year. If you are a California resident and would like to make such a request, please submit your request in writing to us by using the contact details provided in the section 'HOW CAN YOU CONTACT US ABOUT THIS NOTICE?'
              </Typography>
            </SubSection>
          </Section>

          {/* 14. Indian Compliance */}
          <Section title="14. Compliance with Indian Data Protection Laws">
            <Typography paragraph sx={{ fontWeight: 500 }}>
              For Users in India:
            </Typography>
            <Typography paragraph>
              We comply with the Digital Personal Data Protection Act (DPDPA) 2023 and applicable Indian data protection laws.
            </Typography>
            
            <Typography paragraph sx={{ mt: 2, fontWeight: 500 }}>Hosting:</Typography>
            <Typography paragraph>
              Our platform is primarily hosted in India (AWS Mumbai region - ap-south-1). However, we also utilize third-party services that may be hosted in other countries, including the United States, for specific functionalities such as analytics, authentication, and content delivery.
            </Typography>

            <Typography paragraph sx={{ mt: 2, fontWeight: 500 }}>Data Storage:</Typography>
            <Typography paragraph>
              Your personal information is primarily stored on servers located in India (AWS Mumbai region - ap-south-1).
            </Typography>

            <Typography paragraph sx={{ mt: 2, fontWeight: 500 }}>Data Localization:</Typography>
            <Typography paragraph>
              As a majority of our users are based in India, we store and process Indian user data within India in compliance with data localization requirements. However, certain data may be processed by third-party services located outside India as described in this Privacy Notice.
            </Typography>

            <Typography paragraph sx={{ mt: 2, fontWeight: 500 }}>Your Rights:</Typography>
            <Typography paragraph>
              Indian users have the right to:
            </Typography>
            <List items={[
              'Access their personal data',
              'Correct inaccurate personal data',
              'Request erasure of personal data',
              'Nominate another person to exercise rights in case of death or incapacity',
              'Withdraw consent for data processing',
              'File complaints with the Data Protection Board of India'
            ]} />

            <Typography paragraph sx={{ mt: 2, fontWeight: 500 }}>Data Fiduciary:</Typography>
            <Typography paragraph>
              HypeCircle acts as a Data Fiduciary under the DPDPA 2023 for Indian users.
            </Typography>

            <Typography paragraph sx={{ mt: 2, fontWeight: 500 }}>Contact for Indian Users:</Typography>
            <Typography paragraph>
              For data protection queries specific to Indian law, contact us at{' '}
              <Link href="mailto:hype.circle.main@gmail.com" color="primary">
                hype.circle.main@gmail.com
              </Link>
            </Typography>

            <Typography paragraph sx={{ mt: 2, fontWeight: 500 }}>Cross-Border Transfers:</Typography>
            <Typography paragraph>
              When we transfer Indian user data outside India (to third-party services like Google Analytics or Facebook), we ensure appropriate safeguards are in place through Standard Contractual Clauses.
            </Typography>

            <Typography paragraph sx={{ mt: 2, fontWeight: 500 }}>Grievance Officer:</Typography>
            <Typography paragraph>
              For complaints or concerns, Indian users may contact our Grievance Officer at{' '}
              <Link href="mailto:hype.circle.main@gmail.com" color="primary">
                hype.circle.main@gmail.com
              </Link>
              . We will respond within 30 days.
            </Typography>
          </Section>

          {/* 15. Updates */}
          <Section title="15. Do We Make Updates to This Notice?">
            <Typography paragraph sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
              In Short: Yes, we will update this notice as necessary to stay compliant with relevant laws.
            </Typography>
            <Typography paragraph>
              We may update this Privacy Notice from time to time. The updated version will be indicated by an updated 'Revised' date at the top of this Privacy Notice. If we make material changes to this Privacy Notice, we may notify you either by prominently posting a notice of such changes or by directly sending you a notification. We encourage you to review this Privacy Notice frequently to be informed of how we are protecting your information.
            </Typography>
          </Section>

          {/* 16. Contact */}
          <Section title="16. How Can You Contact Us About This Notice?">
            <Typography paragraph>
              If you have questions or comments about this notice, you may email us at{' '}
              <Link href="mailto:hype.circle.main@gmail.com" color="primary">
                hype.circle.main@gmail.com
              </Link>
              {' '}or contact us by post at:
            </Typography>
            <Box sx={{ 
              mt: 3, 
              p: 3, 
              bgcolor: 'action.hover', 
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`
            }}>
              <Typography variant="body1" gutterBottom>
                <strong>HypeCircle</strong>
              </Typography>
              <Typography variant="body1" gutterBottom>
                Hyderabad, Telangana 500085
              </Typography>
              <Typography variant="body1" gutterBottom>
                India
              </Typography>
              <Typography variant="body1" gutterBottom sx={{ mt: 2 }}>
                <strong>Email:</strong>{' '}
                <Link href="mailto:hype.circle.main@gmail.com" color="primary">
                  hype.circle.main@gmail.com
                </Link>
              </Typography>
            </Box>
          </Section>

          {/* 17. Review/Update/Delete */}
          <Section title="17. How Can You Review, Update, or Delete the Data We Collect From You?">
            <Typography paragraph>
              Based on the applicable laws of your country or state of residence in the US, you may have the right to request access to the personal information we collect from you, details about how we have processed it, correct inaccuracies, or delete your personal information.
            </Typography>
            <Typography paragraph>
              To request to review, update, or delete your personal information, please visit:{' '}
              <Link href="https://www.hypecircle.co.in/facebook-data-deletion.html" target="_blank" color="primary">
                https://www.hypecircle.co.in/facebook-data-deletion.html
              </Link>
            </Typography>
          </Section>

          {/* Footer */}
          <Divider sx={{ my: 4 }} />
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              © {new Date().getFullYear()} HypeCircle. All rights reserved.
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              This Privacy Policy is effective as of October 31, 2025
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

// Helper Components
const Section = ({ title, children }) => (
  <Box sx={{ mb: 5 }}>
    <Typography 
      variant="h5" 
      component="h2" 
      gutterBottom 
      sx={{ 
        fontWeight: 600,
        mb: 2,
        color: 'primary.main'
      }}
    >
      {title}
    </Typography>
    {children}
  </Box>
);

const SubSection = ({ title, children }) => (
  <Box sx={{ mb: 3, ml: { xs: 0, md: 2 } }}>
    <Typography 
      variant="h6" 
      component="h3" 
      gutterBottom 
      sx={{ 
        fontWeight: 500,
        mb: 1.5,
        fontSize: '1.1rem'
      }}
    >
      {title}
    </Typography>
    {children}
  </Box>
);

const List = ({ items }) => (
  <Box component="ul" sx={{ pl: 3, mt: 1 }}>
    {items.map((item, index) => (
      <Typography 
        key={index} 
        component="li" 
        paragraph 
        sx={{ mb: 1 }}
      >
        {item}
      </Typography>
    ))}
  </Box>
);

export default PrivacyPolicy;
